/**
 * TCL Method — the pinned, scroll-stepped "TrichoLab Method" cycle diagram.
 *
 * The stage pins under the sticky header for `steps × step_scroll %` of the
 * viewport height. Progress through that distance is cut into one equal slice
 * per step, and crossing a slice boundary PLAYS (does not scrub) the change.
 * WITHIN a slice, two scroll indicators are scrubbed so the visitor can see how
 * close the next step is: the active circle's own border fills clockwise in
 * deep green over its sage (both layouts), and on desktop the arrow leading OUT of the active step fills
 * toward the next circle (Maintain's arrow fills toward the band's end).
 *
 *   desktop (≥990px) — the active step's circle, number and title go to full
 *     opacity and the rest dim; the outgoing paragraph fades out and the
 *     incoming one fades + slides up (Dawn's `slideIn`: 2rem, 600ms,
 *     cubic-bezier(0,0,0.3,1), so it reads like every other reveal on the
 *     site). Every arrow shows a faint track; passed arrows stay drawn.
 *     Clicking (or Enter/Space on) a circle scrolls to that step.
 *
 *   below 990px — the four circles sit on an arc whose centre is off-screen to
 *     the bottom-left. Each step turns the wheel one notch anticlockwise: the
 *     previous circle exits over the top-left, the active one settles upright
 *     under the title, the next one comes up from the bottom-right corner,
 *     where it peeks in. The arc is NOT closed, so after Maintain
 *     nothing peeks and the visitor scrolls straight on.
 *
 * Only after the final step's slice does the pin release.
 *
 * GSAP + ScrollTrigger are loaded ONCE theme-wide from `layout/theme.liquid`
 * (PR #60) — this file must never add a script tag for either.
 *
 * PROGRESSIVE ENHANCEMENT
 * -----------------------
 * The section's CSS renders the finished static diagram by default. This file
 * sets `data-method-on` only after it has mounted, and `gsap.matchMedia()`
 * reverts every inline style, tween and pin it made when the breakpoint or
 * the reduce-motion preference changes. So no JS, GSAP missing and
 * prefers-reduced-motion: reduce all get the full static diagram.
 */

(function () {
  'use strict';

  if (window.__tclMethodLoaded) return;
  window.__tclMethodLoaded = true;

  var DESKTOP = '(min-width: 990px) and (prefers-reduced-motion: no-preference)';
  var MOBILE = '(max-width: 989px) and (prefers-reduced-motion: no-preference)';

  /* Dawn's slideIn curve and timing (assets/base.css, --ease-out-slow and
     --duration-extra-long), so the copy enters like the rest of the site. */
  var EASE_IN = 'cubic-bezier(0, 0, 0.3, 1)';
  var SLIDE_PX = 20;
  var IN_S = 0.6;
  var OUT_S = 0.3;
  var DIM = 0.32;
  /* Opacity of an arrow's faint track (and of its head until the line
     reaches it). Matches `.tl-method__arrow-track` in the section CSS. */
  var TRACK = 0.16;
  /* Scroll distance, at the end of every step, over which the border and the
     outgoing arrow sit full before the next step takes over. */
  var PAUSE_PX = 50;

  /* The mobile wheel: angle between neighbouring circles on the arc, and how
     much of the turn each circle keeps as visible tilt while it travels (it
     is upright again whenever it comes to rest in the active slot). */
  var WHEEL_ACTIVE_DEG = -45;
  var WHEEL_STEP_DEG = 45;
  var WHEEL_TILT = 0.5;
  var WHEEL_S = 0.9;

  /* Mobile scroll damping (2026-10-28). A touch fling carries momentum, and at
     the desktop distance (75% of the screen per step) one flick of the thumb
     ran through all four steps and out of the pin before the wheel had turned
     once. Below 990px each step therefore gets MOBILE_SCROLL_SCALE times the
     configured distance, and when the scroll comes to rest the pin SNAPS to
     the middle of the nearest step's slice (or to its end, which releases
     it), so a visitor always lands on a whole step instead of between two. */
  var MOBILE_SCROLL_SCALE = 1.6;

  var instances = [];

  /* Where the pinned stage's top edge goes: under the always-sticky header
     AND under the sticky "In this page" nav when the page has one (About
     does, the homepage does not). tcl-page-nav.js publishes the nav's stuck
     bottom edge + 16px of breathing room as --tl-anchor-offset; without it
     the nav covered the top of the stage (About's Purify circle on desktop,
     the title on mobile). */
  function topOffset() {
    var cs = getComputedStyle(document.documentElement);
    var header = parseFloat(cs.getPropertyValue('--header-height'));
    var anchor = parseFloat(cs.getPropertyValue('--tl-anchor-offset'));
    header = isNaN(header) ? 0 : header;
    return isNaN(anchor) ? header : Math.max(header, anchor - 16);
  }

  function mount(root) {
    if (root.__tclMethod) return;

    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger || !gsap.matchMedia) return;
    gsap.registerPlugin(ScrollTrigger);

    var stage = root.querySelector('[data-method-stage]');
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-method-step]'));
    if (!stage || steps.length < 2) return;

    var arrows = Array.prototype.slice.call(root.querySelectorAll('[data-method-arrow]'));
    var title = root.querySelector('.tl-method__title');
    var n = steps.length;
    var stepScroll = (parseFloat(root.dataset.stepScroll) || 75) / 100;

    function pinTrigger(onProgress, onReset, opts) {
      opts = opts || {};
      var scale = opts.scale || 1;
      return ScrollTrigger.create({
        trigger: stage,
        pin: stage,
        pinSpacing: true,
        start: function () {
          return 'top ' + topOffset() + 'px';
        },
        end: function () {
          return '+=' + Math.round(window.innerHeight * stepScroll * scale * n);
        },
        snap: opts.snap,
        invalidateOnRefresh: true,
        onUpdate: function (self) {
          onProgress(self.progress, self);
        },
        onEnter: function (self) {
          onProgress(self.progress, self);
        },
        onEnterBack: function (self) {
          onProgress(self.progress, self);
        },
        onLeaveBack: onReset,
      });
    }

    function indexFor(p) {
      return Math.max(0, Math.min(n - 1, Math.floor(p * n)));
    }

    /* How far through the active step's own slice the visitor is, 0..1 —
       reaching 1 PAUSE_PX BEFORE the slice ends, so the full border (and the
       full outgoing arrow) holds for a short beat before the next step takes
       over, instead of the switch landing the instant the fill completes. */
    function localFor(p, self) {
      var slicePx = (self.end - self.start) / n;
      var into = p * (self.end - self.start) - indexFor(p) * slicePx;
      return Math.max(0, Math.min(1, into / Math.max(1, slicePx - PAUSE_PX)));
    }

    /* ---- The border as progress bar (both layouts) ------------------------
       Each circle's border is a conic ring whose deep-green share is the CSS
       custom property --tlm-p (see the section stylesheet). The active step's
       is scrubbed 0 -> 1 through its slice with a short tween, which smooths
       coarse wheel ticks without lagging visibly behind the scroll. Passed
       steps KEEP a full border (they are done, like the arrows they point
       along); steps still ahead stay at 0, the plain border. */
    var frames = steps.map(function (s) {
      return s.querySelector('.tl-method__frame');
    });

    function ring(i, local) {
      frames.forEach(function (f, k) {
        if (!f) return;
        var to = i < 0 ? 0 : k < i ? 1 : k === i ? local : 0;
        gsap.to(f, { '--tlm-p': to, duration: k === i ? 0.15 : 0.3, ease: 'none', overwrite: true });
      });
    }

    function setCurrent(i) {
      steps.forEach(function (s, k) {
        if (k === i) s.setAttribute('aria-current', 'step');
        else s.removeAttribute('aria-current');
      });
    }

    /* Fade the outgoing copy out and play the incoming copy in. `targets` is
       one element per step (the paragraph on desktop, the whole text panel on
       the wheel). Opacity only, never visibility: the inactive copy stays in
       the accessibility tree, so a screen reader gets all four steps. */
    function swapCopy(targets, from, to, inDelay) {
      if (from >= 0 && targets[from]) {
        gsap.to(targets[from], {
          opacity: 0,
          y: -SLIDE_PX / 2,
          duration: OUT_S,
          ease: 'power1.in',
          overwrite: true,
        });
      }
      if (to >= 0 && targets[to]) {
        gsap.fromTo(
          targets[to],
          { opacity: 0, y: SLIDE_PX },
          {
            opacity: 1,
            y: 0,
            duration: IN_S,
            delay: inDelay != null ? inDelay : from >= 0 ? OUT_S * 0.5 : 0,
            ease: EASE_IN,
            overwrite: true,
          }
        );
      }
    }

    /* Tweens started later, from ScrollTrigger callbacks, are NOT recorded by
       the matchMedia context, so its revert would leave their inline styles
       behind (a lit ring, a dimmed circle) on the static diagram it hands
       back to. Each branch's cleanup clears every animated element itself. */
    function clearAll() {
      var els = Array.prototype.slice.call(
        root.querySelectorAll(
          '[data-method-step], [data-method-body], [data-method-text], [data-method-media], ' +
            '.tl-method__frame, .tl-method__arrow-line, .tl-method__arrow-head'
        )
      );
      gsap.killTweensOf(els);
      gsap.set(els, { clearProps: 'all' });
    }

    /* The stage's height and the desktop circle size are CSS calcs off
       --tlm-top, so it is written BEFORE every refresh measures anything. */
    function writeTop() {
      root.style.setProperty('--tlm-top', topOffset() + 'px');
    }
    writeTop();
    ScrollTrigger.addEventListener('refreshInit', writeTop);
    root.__tclMethodWriteTop = writeTop;

    var mm = gsap.matchMedia();

    /* ---- Desktop: the cycle diagram ---------------------------------------- */
    mm.add(DESKTOP, function () {
      var bodies = steps.map(function (s) {
        return s.querySelector('[data-method-body]');
      });
      var lines = arrows.map(function (a) {
        return a.querySelector('.tl-method__arrow-line');
      });
      var heads = arrows.map(function (a) {
        return a.querySelector('.tl-method__arrow-head');
      });
      var current = -1;
      var trigger = null;

      root.setAttribute('data-method-on', '');

      gsap.set(bodies.filter(Boolean), { opacity: 0, y: SLIDE_PX });
      /* Every step starts INACTIVE (dimmed), so the band arrives already
         dimmed and Purify lights up, rather than all four arriving at full
         strength and three of them dropping away at once. */
      gsap.set(steps, { opacity: DIM });
      /* Undrawn lines are also transparent: a fully offset dash still paints
         its round line cap as a stray dot. */
      gsap.set(lines, { strokeDasharray: 1, strokeDashoffset: 1, autoAlpha: 0 });
      gsap.set(heads, { opacity: TRACK });

      /* Arrow j (0-based) runs from step j to step j + 1 (the last one closes
         the loop back to Purify). It is full once step j is passed, FILLS with
         the scroll while step j is active, and is an empty track otherwise.
         The head inks in as the line reaches it. */
      function arrowsTo(i, local) {
        lines.forEach(function (line, j) {
          var f = i < 0 ? 0 : j < i ? 1 : j === i ? local : 0;
          gsap.to(line, {
            strokeDashoffset: 1 - f,
            autoRound: false,
            autoAlpha: f > 0.005 ? 1 : 0,
            duration: 0.15,
            ease: 'none',
            overwrite: true,
          });
          if (heads[j]) {
            gsap.to(heads[j], { opacity: f > 0.97 ? 1 : TRACK, duration: 0.2, overwrite: true });
          }
        });
      }

      /* Click (or Enter / Space on) a circle to jump to that step: scroll to
         just inside the start of its slice and let the pin do the rest. */
      function jumpTo(j) {
        if (!trigger || j === current) return;
        var y = trigger.start + ((trigger.end - trigger.start) * j) / n + 2;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      var media = steps.map(function (s) {
        return s.querySelector('[data-method-media]');
      });
      var handlers = media.map(function (m, j) {
        if (!m) return null;
        var name = steps[j].querySelector('.tl-method__name');
        m.setAttribute('role', 'button');
        m.setAttribute('tabindex', '0');
        m.setAttribute('aria-label', 'Go to step ' + (j + 1) + (name ? ': ' + name.textContent.trim() : ''));
        var onClick = function () {
          jumpTo(j);
        };
        var onKey = function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            jumpTo(j);
          }
        };
        m.addEventListener('click', onClick);
        m.addEventListener('keydown', onKey);
        return { m: m, onClick: onClick, onKey: onKey };
      });

      function go(i) {
        if (i === current) return;
        var prev = current;
        current = i;
        setCurrent(i);
        steps.forEach(function (s, k) {
          gsap.to(s, {
            opacity: k === i ? 1 : DIM,
            duration: 0.5,
            ease: 'power2.out',
            overwrite: 'auto',
          });
        });
        swapCopy(bodies, prev, i);
      }

      trigger = pinTrigger(
        function (p, self) {
          var i = indexFor(p);
          var local = localFor(p, self);
          go(i);
          ring(i, local);
          arrowsTo(i, local);
        },
        function () {
          go(-1);
          ring(-1, 0);
          arrowsTo(-1, 0);
        }
      );

      return function () {
        trigger.kill();
        handlers.forEach(function (h) {
          if (!h) return;
          h.m.removeEventListener('click', h.onClick);
          h.m.removeEventListener('keydown', h.onKey);
          h.m.removeAttribute('role');
          h.m.removeAttribute('tabindex');
          h.m.removeAttribute('aria-label');
        });
        clearAll();
        root.removeAttribute('data-method-on');
        setCurrent(-1);
      };
    });

    /* ---- Below 990px: the wheel ------------------------------------------- */
    mm.add(MOBILE, function () {
      var media = steps.map(function (s) {
        return s.querySelector('[data-method-media]');
      });
      var texts = steps.map(function (s) {
        return s.querySelector('[data-method-text]');
      });
      var current = -1;
      var wheel = { phase: 0 };
      var geo = null;

      root.setAttribute('data-method-on', '');
      gsap.set(texts, { opacity: 0, y: SLIDE_PX });

      /* Geometry is measured from the stage, so it is recomputed on every
         ScrollTrigger refresh (resize, font swap, late images). The wheel's
         centre is off-screen to the BOTTOM-LEFT and the active circle sits at
         WHEEL_ACTIVE_DEG on it (up and to the right of the centre). Turning
         the wheel anticlockwise carries each circle along one curve: in from
         the bottom-right corner, up the right side into the active slot under
         the title, then out over the top-left. The next circle's centre is
         placed just below the stage's bottom edge, so it peeks in at the
         bottom-right corner; that fixes the radius. */
      function measure() {
        var w = stage.clientWidth;
        var h = stage.clientHeight;
        var titleBottom = title ? title.offsetTop + title.offsetHeight : 0;
        var d = Math.min(w * 0.64, h * 0.34, 280);
        var activeY = titleBottom + Math.max(16, h * 0.03) + d / 2;
        var nextY = h + d * 0.15;
        var a0 = (WHEEL_ACTIVE_DEG * Math.PI) / 180;
        var a1 = ((WHEEL_ACTIVE_DEG + WHEEL_STEP_DEG) * Math.PI) / 180;
        var r = (nextY - activeY) / (Math.sin(a1) - Math.sin(a0));
        geo = { d: d, r: r, cx: w / 2 - r * Math.cos(a0), cy: activeY - r * Math.sin(a0) };
        root.style.setProperty('--tlm-m-d', d + 'px');
        root.style.setProperty('--tlm-m-text-top', activeY + d / 2 + 20 + 'px');
        render();
      }

      function render() {
        if (!geo) return;
        media.forEach(function (m, i) {
          var off = i - wheel.phase;
          var theta = ((WHEEL_ACTIVE_DEG + off * WHEEL_STEP_DEG) * Math.PI) / 180;
          gsap.set(m, {
            x: geo.cx + geo.r * Math.cos(theta) - geo.d / 2,
            y: geo.cy + geo.r * Math.sin(theta) - geo.d / 2,
            rotation: off * WHEEL_STEP_DEG * WHEEL_TILT,
            /* The leaving circle is gone by just past half its swing, before
               it reaches the title; anything further back stays at zero, so
               Purify can never peek back in after Maintain. */
            opacity: off >= 0 ? 1 - Math.min(off, 1) * 0.35 : Math.max(0, 1 + off * 1.8),
          });
        });
      }

      function go(i) {
        if (i === current) return;
        var prev = current;
        current = i;
        setCurrent(i);
        gsap.to(wheel, {
          phase: Math.max(0, i),
          duration: WHEEL_S,
          ease: 'power2.inOut',
          overwrite: true,
          onUpdate: render,
        });
        /* The incoming circle sweeps up THROUGH the text slot, so its copy
           waits until the turn has mostly landed. */
        swapCopy(texts, prev, i, prev >= 0 ? WHEEL_S * 0.55 : 0);
      }

      measure();
      ScrollTrigger.addEventListener('refresh', measure);

      /* Snap points: the middle of every step's slice, plus 1 (the end, so a
         visitor scrolling on is let out of the pin rather than pulled back). */
      var snapPoints = steps.map(function (s, i) {
        return (i + 0.5) / n;
      });
      snapPoints.push(1);

      var trigger = pinTrigger(
        function (p, self) {
          var i = indexFor(p);
          go(i);
          ring(i, localFor(p, self));
        },
        function () {
          go(-1);
          ring(-1, 0);
        },
        {
          scale: MOBILE_SCROLL_SCALE,
          snap: { snapTo: snapPoints, duration: { min: 0.2, max: 0.6 }, delay: 0.08, ease: 'power1.inOut' },
        }
      );

      return function () {
        trigger.kill();
        clearAll();
        ScrollTrigger.removeEventListener('refresh', measure);
        root.removeAttribute('data-method-on');
        root.style.removeProperty('--tlm-m-d');
        root.style.removeProperty('--tlm-m-text-top');
        setCurrent(-1);
      };
    });

    root.__tclMethod = mm;
    instances.push(root);
  }

  function destroy(scope) {
    for (var i = instances.length - 1; i >= 0; i--) {
      var root = instances[i];
      if (!scope || scope.contains(root)) {
        root.__tclMethod.revert();
        delete root.__tclMethod;
        window.ScrollTrigger.removeEventListener('refreshInit', root.__tclMethodWriteTop);
        delete root.__tclMethodWriteTop;
        root.style.removeProperty('--tlm-top');
        instances.splice(i, 1);
      }
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-tcl-method]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: a section is re-rendered wholesale, so the old pin would sit
     on a detached element and leak its pin-spacer (the PR #60 failure). */
  document.addEventListener('shopify:section:load', function (e) {
    destroy(e.target);
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroy(e.target);
  });

  /* ScrollTrigger measures at creation, before lazy images and webfonts have
     moved the page (a 504px drift was measured for tcl-procedure-steps). This
     pin sits BELOW scroll-video's own pin-spacer on the homepage, so it is
     exposed to that drift too. `refresh()` is global and idempotent. */
  function refreshTriggers() {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  }
  if (document.readyState === 'complete') refreshTriggers();
  else window.addEventListener('load', refreshTriggers, { once: true });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(refreshTriggers);
  }
})();
